import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { apiFetch, ApiError } from "../../lib/api";
import { ADMIN_DASHBOARD_PATH } from "../../lib/adminPaths";

/** @typedef {{
 *   id: string,
 *   name: string,
 *   email: string,
 *   createdAt: string,
 * }} NewsletterSubscriber */

function formatSignedUpAt(value) {
  if (!value) {
    return "—";
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

export default function AdminNewsletterSubscribersPage() {
  const { logout } = useAdminAuth();
  const [subscribers, setSubscribers] = useState(/** @type {NewsletterSubscriber[]} */ ([]));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadSubscribers = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const body = await apiFetch("/admin/newsletter-subscribers");
      setSubscribers(body.data.subscribers || []);
    } catch (err) {
      setSubscribers([]);
      setError(err instanceof ApiError ? err.message : "Couldn't load subscribers — try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    void loadSubscribers();
  }, [loadSubscribers]);

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div>
          <p className="admin-eyebrow">ADMIN</p>
          <h1 className="admin-title admin-title--compact">Stay in Touch</h1>
        </div>
        <button className="admin-button admin-button--ghost" type="button" onClick={() => void logout()}>
          Sign out
        </button>
      </header>

      <p>
        <Link className="admin-back-link" to={ADMIN_DASHBOARD_PATH}>
          ← Back to dashboard
        </Link>
      </p>

      <section className="admin-card">
        <p className="admin-lede">
          People who signed up from the footer <strong>Stay in Touch</strong> form.
        </p>
        <p className="admin-muted">Newest sign-ups appear first.</p>

        {error ? <p className="admin-error">{error}</p> : null}

        {loading ? (
          <p className="admin-muted admin-subscriber-table__status">Loading subscribers…</p>
        ) : subscribers.length === 0 ? (
          <p className="admin-muted admin-subscriber-table__status">No sign-ups yet.</p>
        ) : (
          <div className="admin-subscriber-table-wrap">
            <table className="admin-subscriber-table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Email</th>
                  <th scope="col">Signed up</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.map((subscriber) => (
                  <tr key={subscriber.id}>
                    <td>{subscriber.name}</td>
                    <td>
                      <a href={`mailto:${subscriber.email}`}>{subscriber.email}</a>
                    </td>
                    <td>{formatSignedUpAt(subscriber.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
